import { useState } from "react";
import { Loader2, Plus, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { cn } from "@/lib/utils";
import type { Assistant, AssistantTool, ToolKind } from "../api";
import { useAssistantTools, useDeleteTool, useSaveTool } from "../hooks";
import ToolEditor from "./tools/ToolEditor";
import ToolRuns from "./tools/ToolRuns";
import { TOOL_KINDS, toolMeta } from "./tools/kinds";
import { runsOnAttachedNumbers } from "./tools/help";

/** Tools tab: everything this assistant can do beyond talking. */
export default function AssistantTools({ assistant }: { assistant: Assistant }) {
  const q = useAssistantTools(assistant.id);
  const saveMut = useSaveTool(assistant.id);
  const deleteMut = useDeleteTool(assistant.id);
  const [editing, setEditing] = useState<{ tool?: AssistantTool; kind: ToolKind } | null>(
    null,
  );

  const tools = q.data ?? [];
  const used = new Set(tools.map((t) => t.kind));

  return (
    <div className="space-y-6">
      <div className="rounded-xl border bg-card p-4">
        <div className="text-sm font-medium">Add a tool</div>
        <p className="mt-1 text-xs text-muted-foreground">
          Tools run during the call. The assistant decides when to use one from
          the description you give it.
        </p>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {TOOL_KINDS.map((meta) => {
            const taken = meta.once && used.has(meta.kind);
            // Say up front when a tool wouldn't run on this assistant's own
            // numbers — better than discovering it after a test call.
            const talkOnly = !runsOnAttachedNumbers(meta.kind, assistant);
            const Icon = meta.icon;
            return (
              <button
                key={meta.kind}
                disabled={taken}
                onClick={() => setEditing({ kind: meta.kind })}
                className={cn(
                  "flex items-start gap-3 rounded-lg border p-3 text-left transition-colors",
                  taken
                    ? "cursor-default opacity-50"
                    : "hover:border-primary/50 hover:bg-muted/50",
                )}
              >
                <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5 text-sm font-medium">
                    {meta.label}
                    {!taken && <Plus className="size-3 text-muted-foreground" />}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    {taken ? "Already added" : meta.blurb}
                  </div>
                  {!taken && talkOnly && (
                    <div className="mt-1 text-[11px] text-amber-600 dark:text-amber-500">
                      Doesn't run on this assistant's number — Talk only
                    </div>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {q.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-24 w-full" />
        </div>
      ) : tools.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center text-sm text-muted-foreground">
            No tools yet. The assistant can only talk until you add one.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {tools.map((tool) => (
            <ToolCard
              key={tool.id}
              tool={tool}
              onEdit={() => setEditing({ tool, kind: tool.kind })}
              onToggle={(is_active) => saveMut.mutate({ id: tool.id, is_active })}
              onDelete={() => deleteMut.mutate(tool.id)}
              deleting={deleteMut.isPending}
            />
          ))}
        </div>
      )}

      {editing && (
        <ToolEditor
          assistant={assistant}
          kind={editing.kind}
          tool={editing.tool}
          open
          onOpenChange={(open) => !open && setEditing(null)}
        />
      )}
    </div>
  );
}

function ToolCard({
  tool,
  onEdit,
  onToggle,
  onDelete,
  deleting,
}: {
  tool: AssistantTool;
  onEdit: () => void;
  onToggle: (active: boolean) => void;
  onDelete: () => void;
  deleting: boolean;
}) {
  const meta = toolMeta(tool.kind);
  const Icon = meta.icon;
  const [runsOpen, setRunsOpen] = useState(false);

  return (
    <div className="rounded-xl border bg-card p-4">
      <div className="flex items-start gap-3">
        <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium">{meta.label}</span>
            {tool.kind === "api_request" && (
              <code className="rounded bg-muted px-1.5 py-0.5 font-mono text-[11px]">
                {tool.name}
              </code>
            )}
            {!tool.is_active && (
              <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] text-muted-foreground">
                Off
              </span>
            )}
          </div>
          <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
            {tool.description}
          </p>
          <ToolSummary tool={tool} />
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Switch
            checked={tool.is_active}
            onCheckedChange={onToggle}
            aria-label="Tool enabled"
          />
          <Button variant="outline" size="sm" onClick={onEdit}>
            Edit
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="text-muted-foreground hover:text-destructive"
            disabled={deleting}
            onClick={onDelete}
            aria-label="Remove tool"
          >
            {deleting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Trash2 className="size-4" />
            )}
          </Button>
        </div>
      </div>

      {tool.kind === "api_request" && (
        <div className="mt-3 border-t pt-3">
          <button
            className="text-xs text-muted-foreground underline-offset-2 hover:text-foreground hover:underline"
            onClick={() => setRunsOpen((v) => !v)}
          >
            {runsOpen ? "Hide recent runs" : "Recent runs"}
          </button>
          {runsOpen && <ToolRuns toolId={tool.id} />}
        </div>
      )}
    </div>
  );
}

/** The one line that says what this tool will actually do on a call. */
function ToolSummary({ tool }: { tool: AssistantTool }) {
  const c = tool.config || {};
  let text = "";
  if (tool.kind === "api_request") {
    text = `${(c.method || "POST").toUpperCase()} ${c.url || ""}`;
  } else if (tool.kind === "transfer_call") {
    text = c.destination ? `Transfers to ${c.destination}` : "";
  } else if (tool.kind === "send_text") {
    text =
      c.recipient === "fixed"
        ? `Texts ${c.number || "a fixed number"}`
        : "Texts the caller's own number";
  } else if (tool.kind === "end_call") {
    text = c.message ? `Says "${c.message}" first` : "Hangs up after the closing line";
  } else if (tool.kind === "press_digit") {
    text = `Waits ${((c.delay_ms ?? 1000) / 1000).toFixed(1)} s after a prompt, then presses`;
  } else if (tool.kind === "calcom" || tool.kind === "odoo") {
    text = "Uses this workspace's connection in Settings → Integrations";
  }
  if (!text.trim()) return null;
  return (
    <p className="mt-1.5 truncate font-mono text-[11px] text-muted-foreground">{text}</p>
  );
}
